import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { BaseTabs, BaseDrawer } from '@/components';
import { SensorFusion } from '@/modules/AssetsStudioPage/SensorFusion';
import { SENSOR_DATA_INNER_TABS, SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { OilReportTable } from '../ReportsData/OilReportTable';
import { Waveform } from './Waveform';
import { useAppFilters, useFetchReports, useQueryNavigation, useFetchFaults } from '@/hooks';
import { useParams } from 'react-router-dom';
import { useFetchSensorDataMetaDataQuery } from '@/store/api/dataLayersApi';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';
import { SenorDataRightPaneMenu } from './components/SenorDataRightPaneMenu';
import { Skeleton } from '@/components/ui/skeleton';
import { format } from 'date-fns';
import { DIAGNOSTICS_DATE_FORMAT } from '@/const';
import type { RootState } from '@/store/store';
import { useVerticalLines } from '@/hooks/shared/DataLayers';
import { useBenchmarkLines } from '@/hooks/shared/DataLayers/useBenchmarkLines';
import { useItemsDrawer } from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetPreviewTabsViews/Faults/hooks/useItemsDrawer';
import { ItemsDrawerContent } from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetPreviewTabsViews/Faults/components/ItemsDrawerContent';
import { Icon } from '@/components/shared/display/Icon';
import { ChevronRightSmallIcon, CloseIcon } from '@/assets/Icons';

interface Component {
  component_id: string;
  [key: string]: unknown;
}

interface SensorDataProps {
  asset: Record<string, unknown>;
  components: Component[];
  diagnostics: unknown[];
}

/**
 * `NewSensorData`
 *
 * A dynamic, tabbed dashboard view for sensor-level diagnostics within the Asset Studio page.
 * This component renders different sections—Sensor Fusion, Spectrum, and Oil Report—based on the selected tab.
 * It also adapts behavior based on asset type (fixed vs. mobile) and dynamically fetches sensor metadata and reports.
 *
 * ### Features:
 * - Fetches and memoizes sensor configuration using `useFetchSensorDataMetaDataQuery`
 * - Displays tab content based on API-provided sensor tab config (`config.tabs`)
 * - Controls data-layer visibility and operational modes via `SensorFusion` + right-pane menu
 * - Supports fallback skeleton UI while loading
 *
 * @param asset - The currently selected asset object from upstream context
 * @param components - Array of components belonging to the asset
 * @param diagnostics - Diagnostic records related to the asset
 *
 * @returns Rendered JSX view of tabbed sensor diagnostic sections
 */
export const NewSensorData = ({ asset, components, diagnostics }: SensorDataProps) => {
  const { siteId, id: assetId } = useParams();
  const {
    currentParams: { selectedPart = null, tab: urlTab = null },
    navigateWithQuery,
    pathname,
  } = useQueryNavigation();
  const { isMobileAsset } = useActiveAssetsType();
  const { selectedComponent: selectedComponentId } = useAppFilters();
  const {
    dateRange: { from, to },
  } = useSelector((state: RootState) => state.assetsStudio);

  const asset_name = typeof asset?.asset_name === 'string' ? asset.asset_name : '';
  const asset_model = typeof asset?.asset_model === 'string' ? asset.asset_model : '';
  const asset_supplier = typeof asset?.asset_supplier === 'string' ? asset.asset_supplier : '';

  // Determine if metadata query should be skipped based on asset completeness and type
  const skipQuery = useMemo(() => {
    // Skip if essential params are missing
    if (!siteId || !assetId) return true;
    // For mobile assets, wait for all required asset data
    if (isMobileAsset) {
      return !asset_name || !asset_model || !asset_supplier;
    }
    // For fixed assets, only need asset_name
    return !asset_name;
  }, [siteId, assetId, isMobileAsset, asset_name, asset_model, asset_supplier]);

  const queryParams = useMemo(() => {
    const params = {
      site_id: siteId!,
      asset_id: assetId!,
      component_id: selectedComponentId,
      subcomponent_id: !selectedPart || selectedPart.includes('custom-part-analysis') ? null : selectedPart,
      asset_category: isMobileAsset ? 'mobile' : 'fixed',
      asset_name,
      asset_model,
      asset_supplier,
      from_date: from ? format(new Date(from), DIAGNOSTICS_DATE_FORMAT) : null,
      to_date: to ? format(new Date(to), DIAGNOSTICS_DATE_FORMAT) : null,
    };
    return params;
  }, [
    siteId,
    assetId,
    selectedComponentId,
    selectedPart,
    isMobileAsset,
    asset_name,
    asset_model,
    asset_supplier,
    from,
    to,
  ]);

  const {
    data: config,
    isLoading,
    refetch,
    isFetching,
  } = useFetchSensorDataMetaDataQuery(queryParams, {
    skip: skipQuery,
    refetchOnMountOrArgChange: true, // Force refetch when args change
  });

  const { isLoading: isFaultsLoading, faultsRaw: faults, error } = useFetchFaults();

  // Track the component ID that the current config belongs to
  const [configComponentId, setConfigComponentId] = useState<string | undefined>(undefined);

  // Update the tracked component ID when we get fresh data
  useEffect(() => {
    if (config && !isFetching && selectedComponentId) {
      setConfigComponentId(selectedComponentId);
    }
  }, [config, isFetching, selectedComponentId]);

  // Only use config if it matches the current component
  const displayConfig = useMemo(() => {
    if (isFetching) {
      // While fetching, don't show any config to avoid showing wrong data
      return undefined;
    }
    if (config && configComponentId === selectedComponentId) {
      return config;
    }
    return undefined;
  }, [config, configComponentId, selectedComponentId, isFetching]);

  const sensorFusionDataLayers = displayConfig?.tabs?.sensorFusion?.dataLayers ?? [];

  const [activeOperationalModes, setActiveOperationalModes] = useState<string[]>([]);
  const [activeDataLayers, setActiveDataLayers] = useState<string[]>([]);
  const [triggerRefresh, setTriggerRefresh] = useState(0);
  const [currentTab, setCurrentTab] = useState<string>(urlTab === 'oilReports' ? 'oilReports' : 'sensorFusion');
  const [displayMode, setDisplayMode] = useState<'sensor-fusion' | 'spectrum' | 'waveform'>('sensor-fusion'); // Display mode for CSS-based visibility
  const [selectedMarkerTimestamp, setSelectedMarkerTimestamp] = useState<string | null>(null); // Track selected marker from Spectrum for highlighting in SensorFusion
  const [selectedTimestampsSet, setSelectedTimestampsSet] = useState<Set<string>>(new Set()); // Track multiple selected timestamps in focus mode
  const previousTabRef = useRef<string | null>(null);
  const prevComponentRef = useRef<string | undefined>(undefined);
  const prevPartRef = useRef<string | null>(null);

  // Separate vertical lines states for each tab
  const sensorFusionVerticalLinesState = useVerticalLines();
  const spectrumVerticalLinesState = useVerticalLines();

  // CRITICAL: Combine vertical lines from BOTH tabs (Sensor Fusion AND Spectrum) for benchmark tracking
  // Markers can be added in either tab but need to be tracked globally for benchmark functionality
  const combinedVerticalLines = useMemo(() => {
    return [...sensorFusionVerticalLinesState.verticalLines, ...spectrumVerticalLinesState.verticalLines];
  }, [sensorFusionVerticalLinesState.verticalLines, spectrumVerticalLinesState.verticalLines]);

  // Shared benchmark state that persists across component switches (Motor, Compressor, Fan, etc.)
  // This is lifted to NewSensorData level because it needs to survive component changes in URL
  // Use the hook instead of plain useState to get auto-initialization and global cache syncing
  const { benchmarkLineBySection, setBenchmarkLineBySection } = useBenchmarkLines(combinedVerticalLines);

  // Sync currentTab with URL tab parameter (e.g., when navigating via oil report icon)
  useEffect(() => {
    if (urlTab && urlTab !== currentTab) {
      setCurrentTab(urlTab);
    }
  }, [urlTab]);

  // Detect component/part changes and force metadata refetch
  useEffect(() => {
    const hasComponentChanged =
      prevComponentRef.current !== undefined && prevComponentRef.current !== selectedComponentId;
    const hasPartChanged = prevPartRef.current !== null && prevPartRef.current !== selectedPart;

    if (hasComponentChanged || hasPartChanged) {
      // RTK Query will automatically abort the previous request when refetch is called
      // Only refetch if the query has been started (not skipped and either has data or is loading)
      if (refetch && !skipQuery && (config || isLoading || isFetching)) {
        refetch();
      }
    }

    prevComponentRef.current = selectedComponentId;
    prevPartRef.current = selectedPart;
  }, [selectedComponentId, selectedPart, refetch]);

  // Initialize default enabled data layers from the config
  useEffect(() => {
    if (sensorFusionDataLayers.length > 0) {
      const enabled = sensorFusionDataLayers.filter((el: any) => el.enabled).map((el: any) => el.id);
      setActiveDataLayers(enabled);
    }
  }, [sensorFusionDataLayers]);

  const selectedComponent = useMemo(() => {
    if (!Array.isArray(components)) return undefined;
    return components.find((c) => c.component_id === selectedComponentId);
  }, [components, selectedComponentId]);

  // Memoize the faults array to prevent creating new references when undefined
  const stableFaults = useMemo(() => faults ?? [], [faults]);
  const { isOpen, view, openList, openDetail, closeDrawer, showBreadcrumb } = useItemsDrawer(stableFaults);

  const { oilReport, isLoadingOilReport, isFetchingOilReport } = useFetchReports({
    asset_name,
    site_id: siteId!,
    component_id: selectedComponent?.component_id,
  });

  // Initialize current tab on first load - MUST be before any conditional returns for React hooks rules
  useEffect(() => {
    // Only set initial tab if not already set
    if (!currentTab && displayConfig?.tabs) {
      const tabs = Object.keys(displayConfig.tabs).filter((tabKey) => {
        const tab = displayConfig.tabs[tabKey as keyof typeof displayConfig.tabs];
        return tab != null;
      });

      const initialTab = tabs[0] || SENSOR_DATA_INNER_TABS.SENSOR_FUSION;
      setCurrentTab(initialTab);
      previousTabRef.current = initialTab;
    }
  }, [displayConfig, currentTab]);

  // Render loading skeleton if query is skipped, still loading, or config doesn't match current component
  if (skipQuery || isLoading || isFetching || !displayConfig?.tabs?.sensorFusion) {
    return (
      <div className="flex flex-col justify-start items-center w-full gap-2 min-h-0 flex-1">
        <div className="w-full h-12">
          <Skeleton className="w-full h-full" />
        </div>
        <div className="w-full min-h-0 flex flex-col gap-2 flex-1">
          <div className="w-full flex-1">
            <Skeleton className="w-full h-full" />
          </div>
          <div className="w-full flex-1">
            <Skeleton className="w-full h-full" />
          </div>
          <div className="w-full flex-1">
            <Skeleton className="w-full h-full" />
          </div>
        </div>
      </div>
    );
  }

  /**
   * Maps each tab key to its corresponding view/component
   * NOTE: sensor fusion/spectrum/waveform have null element - rendered via renderFixedElement
   */
  const tabsToComponent: Record<string, React.ReactNode> = {
    [SENSOR_DATA_INNER_TABS.SENSOR_FUSION]: null,
    [SENSOR_DATA_INNER_TABS.SEPECTRUM]: null,
    [SENSOR_DATA_INNER_TABS.WAVEFORM]: null,
    [SENSOR_DATA_INNER_TABS.OIL_REPORT]: (
      <OilReportTable
        oilReportData={oilReport}
        isLoading={isLoadingOilReport || isFetchingOilReport}
        component={selectedComponent}
      />
    ),
  };

  /**
   * Render fixed content that stays mounted for all tabs
   * This keeps SensorFusion and Waveform mounted when switching tabs (CSS visibility)
   */
  const renderFixedContent = () => {
    const isVibrationAnalysisTab =
      currentTab === SENSOR_DATA_INNER_TABS.SENSOR_FUSION ||
      currentTab === SENSOR_DATA_INNER_TABS.SEPECTRUM ||
      currentTab === SENSOR_DATA_INNER_TABS.WAVEFORM;
    const isWaveformTab = currentTab === SENSOR_DATA_INNER_TABS.WAVEFORM;
    const isOilReportTab = currentTab === SENSOR_DATA_INNER_TABS.OIL_REPORT;

    // Waveform data layers - only WAVEFORM (SENSORS comes from SensorFusion above)
    const waveformActiveDataLayers = [SENSOR_FUSION_DATA_LAYERS.WAVEFORM];

    return (
      <div className="flex flex-col h-[calc(100%-2.8rem)] gap-4 relative overflow-y-auto no-scrollbar !mt-0 pt-2">
        {/* SensorFusion (Vibration Analysis) - shows for SensorFusion, Spectrum, and Waveform tabs */}
        {displayConfig?.tabs?.sensorFusion &&
          (() => {
            return (
              <div style={{ display: isVibrationAnalysisTab ? 'block' : 'none' }}>
                <SensorFusion
                  asset={asset}
                  components={components}
                  diagnostics={diagnostics}
                  config={displayConfig.tabs.sensorFusion}
                  activeOperationalModes={activeOperationalModes}
                  activeDataLayers={activeDataLayers}
                  triggerRefresh={triggerRefresh}
                  displayMode={displayMode}
                  spectrumConfig={displayConfig.tabs.spectrum}
                  selectedMarkerTimestamp={selectedMarkerTimestamp}
                  setSelectedMarkerTimestamp={setSelectedMarkerTimestamp}
                  selectedTimestampsSet={selectedTimestampsSet}
                  setSelectedTimestampsSet={setSelectedTimestampsSet}
                  sensorFusionVerticalLinesState={sensorFusionVerticalLinesState}
                  spectrumVerticalLinesState={spectrumVerticalLinesState}
                  benchmarkLineBySection={benchmarkLineBySection}
                  setBenchmarkLineBySection={setBenchmarkLineBySection}
                />
              </div>
            );
          })()}

        {/* Waveform - always mounted (when config exists), CSS controls visibility */}
        {displayConfig?.tabs?.waveform &&
          (() => {
            return (
              <div style={{ display: isWaveformTab ? 'block' : 'none' }}>
                <Waveform
                  asset={asset}
                  components={components}
                  diagnostics={diagnostics}
                  config={displayConfig.tabs.waveform}
                  activeOperationalModes={activeOperationalModes}
                  activeDataLayers={waveformActiveDataLayers}
                  triggerRefresh={triggerRefresh}
                  sensorFusionVerticalLines={sensorFusionVerticalLinesState}
                  selectedMarkerTimestamp={selectedMarkerTimestamp}
                  setSelectedMarkerTimestamp={setSelectedMarkerTimestamp}
                  masterSensorColorMap={{}}
                />
              </div>
            );
          })()}

        {/* Oil Report - only rendered when active */}
        {isOilReportTab && tabsToComponent[SENSOR_DATA_INNER_TABS.OIL_REPORT]}
      </div>
    );
  };

  /**
   * Dynamically builds tab definition array based on enabled tabs from config
   */
  const sensorDataInnerTabs = displayConfig?.tabs
    ? Object.keys(displayConfig.tabs)
        .filter((tabKey) => {
          const tab = displayConfig.tabs[tabKey as keyof typeof displayConfig.tabs];
          // Hide waveform tab
          return tab != null && tabKey !== SENSOR_DATA_INNER_TABS.WAVEFORM;
        })
        .map((tabKey) => {
          const tab = displayConfig.tabs[tabKey as keyof typeof displayConfig.tabs];
          return {
            id: tabKey,
            title: tab.title,
            disabled: !tab.enabled,
            element: tabsToComponent[tabKey] ?? <div>Coming Soon</div>,
          };
        })
    : [];

  return (
    <div className="overflow-hidden">
      <BaseTabs
        tabs={sensorDataInnerTabs}
        defaultValue={currentTab || (sensorDataInnerTabs[0]?.id ?? '')}
        variant="secondary-plain"
        updateUrl={false}
        onClick={(tabId: string) => {
          // Set display mode based on tab - modes control visibility via CSS, not re-rendering
          if (tabId === SENSOR_DATA_INNER_TABS.SEPECTRUM) {
            setDisplayMode('spectrum');
          } else if (tabId === SENSOR_DATA_INNER_TABS.SENSOR_FUSION) {
            setDisplayMode('sensor-fusion');
          } else if (tabId === SENSOR_DATA_INNER_TABS.WAVEFORM) {
            setDisplayMode('waveform');
          }

          previousTabRef.current = tabId;
          setCurrentTab(tabId);

          // Keep URL tab param in sync so external navigation (oil icon) can detect changes
          navigateWithQuery(pathname, { replaceParams: { tab: tabId } });
        }}
        classNames={{
          tabList: { container: 'rounded-lg h-[2.8rem]' },
        }}
        renderRightPane={() =>
          (currentTab === 'sensorFusion' || currentTab === 'spectrum' || currentTab === 'waveform') && displayConfig ? (
            <SenorDataRightPaneMenu
              opeartionalModes={displayConfig.operational_mode}
              activeOperationalModes={activeOperationalModes}
              activeDataLayers={activeDataLayers}
              sensorFusionDataLayers={sensorFusionDataLayers}
              setActiveOperationalModes={setActiveOperationalModes}
              setActiveDataLayers={setActiveDataLayers}
              hideDataLayersDropdown={currentTab === 'spectrum' || currentTab === 'waveform'}
            />
          ) : null
        }
        renderFixedElement={renderFixedContent}
      />
      {!isFaultsLoading && faults?.length && (
        <BaseDrawer
          isOpen={isOpen}
          onClose={closeDrawer}
          rounded={false}
          showHeaderDivider
          className="border-0 border-l border-neutral-100 min-w-[40vw] w-[40vw]"
          title={
            showBreadcrumb ? (
              <div className="flex items-center font-medium text-base text-[#01152B] gap-1">
                <button onClick={openList} className="">
                  <>{selectedComponent?.component_name} Faults</>
                </button>
                {view?.type === 'DETAIL' && (
                  <>
                    <Icon icon={ChevronRightSmallIcon} color="#55687D" />
                    <span>{view.itemName}</span>
                  </>
                )}
              </div>
            ) : (
              `${selectedComponent?.component_name} Faults`
            )
          }
          direction="right"
          content={
            <ItemsDrawerContent
              items={faults}
              drawerView={view}
              onOpenDetail={openDetail}
              showViewDataLink={false} // Already on sensor data page
            />
          }
        >
          <Icon icon={CloseIcon} />
        </BaseDrawer>
      )}
    </div>
  );
};
